/**
 * #1680 — back/forward-cache (bfcache) session lifecycle for the client-node shell.
 *
 * ## The bug this exists to fix
 *
 * `main.ts` used to tear the whole client stack down on `pagehide` — kernel RPC client,
 * kernel host, service-worker message bridge, background-sync channel — with a listener
 * registered `{ once: true }` and no inspection of `event.persisted`, and there was no
 * `pageshow` handler anywhere in production code.
 *
 * On a bfcache navigation (iOS swipe-back is the common one, but every modern browser does
 * this) the document is *frozen*, not destroyed: `pagehide` fires with `persisted === true`,
 * and on return the SAME document is restored with its DOM, its adopted stylesheets and its
 * JS heap intact — but every connection we closed on the way out stays closed. The user got
 * back a fully rendered, completely dead shell: no RPC client, no kernel, no sync, and no
 * affordance to recover short of a manual reload.
 *
 * ## Why "tear down and rebuild", not "skip teardown when persisted"
 *
 * The tempting fix is to check `event.persisted` and skip teardown so the live session
 * survives the freeze. That is the wrong trade here, for a concrete reason: the kernel host
 * is the single identity/kernel owner and holds a Web Lock for that ownership. A bfcached
 * document that keeps its session alive holds that lock indefinitely while frozen — so a
 * NEW document (a fresh tab, or this same tab after a real navigation) races its handover
 * window against a predecessor that is suspended and cannot yield. That is exactly the
 * #1660 failure — a latched `writer-conflict`, which #1660 then cached — and skipping
 * teardown would let a page compound it against *itself*.
 *
 * So teardown stays unconditional on `pagehide`: leaving the freeze always releases
 * ownership. The missing half is rebuilding on the way back in, which is what `pageshow`
 * with `persisted === true` does here.
 *
 * ## Why the listeners are not `{ once: true }`
 *
 * A restored page can be hidden again, and again — swipe back, swipe forward, swipe back.
 * With `{ once: true }` only the first `pagehide` ever tore down, so every cycle after the
 * first leaked a live kernel host into the bfcache with its Web Lock held: the precise
 * condition the unconditional teardown above exists to prevent. Both listeners are
 * therefore persistent, and `teardown` is required to be idempotent so a `pagehide`
 * following a page that was never restored is a no-op rather than a double-close.
 */

/**
 * One live client stack.
 *
 * `teardown` MUST be idempotent (it may be called more than once) and MUST resolve only
 * once the session has actually released its resources — in particular the kernel host's
 * identity-ownership Web Lock. The returned promise is what lets a restore serialize behind
 * the freeze that preceded it: closing the kernel host is asynchronous, so a rebuild that
 * did not await it would race its own predecessor for the lock and the new kernel would
 * stall waiting on a lock the outgoing one had not finished dropping. That is not
 * hypothetical — it is what the #1680 E2E spec caught on the first cut of this fix.
 */
export interface ClientSession {
  readonly teardown: () => Promise<void>;
}

export interface SessionLifecycleOptions {
  /**
   * Builds a fresh client stack on bfcache restore. Returning `undefined` declines the
   * rebuild (used when the initial boot never mounted a shell — e.g. it ended on the skin
   * failure card, which carries its own Retry affordance and must not be overwritten).
   */
  readonly restart: () => ClientSession | undefined;
  /** The already-running session, if the initial boot produced one. */
  readonly session: ClientSession | undefined;
  /** Injected for testability; production passes `globalThis`. */
  readonly target: Pick<EventTarget, "addEventListener" | "removeEventListener">;
}

/**
 * Wires the persistent `pagehide`/`pageshow` pair. Returns a disposer that removes both
 * listeners and tears the current session down (used by tests; production keeps it for the
 * lifetime of the document).
 */
export function installSessionLifecycle(options: SessionLifecycleOptions): () => Promise<void> {
  let current = options.session;
  let live = current !== undefined;
  /** Resolves when the most recent teardown has fully released its resources. */
  let released: Promise<void> = Promise.resolve();

  const onPageHide = (): void => {
    if (!live) return;
    live = false;
    const closing = current?.teardown() ?? Promise.resolve();
    // Swallow: a teardown failure must not leave an unhandled rejection on a page that is
    // being frozen, and a restore should still be attempted afterwards regardless.
    released = closing.catch(() => undefined);
  };

  const onPageShow = (event: Event): void => {
    // `pageshow` also fires on every ordinary load with `persisted === false`, where the
    // session the initial boot just built is already live — rebuilding there would double
    // the kernel host. Only a genuine bfcache restore needs a rebuild.
    if (!isPersistedRestore(event)) return;
    if (live) return;
    // Claimed up front so a second `pageshow` arriving while the rebuild is still awaiting
    // the previous teardown cannot start a second, competing stack.
    live = true;
    void (async () => {
      // Serialize behind the freeze: the outgoing kernel host must have dropped its Web
      // Lock before the incoming one tries to take it. See `ClientSession.teardown`.
      await released;
      current = options.restart();
      live = current !== undefined;
    })();
  };

  options.target.addEventListener("pagehide", onPageHide);
  options.target.addEventListener("pageshow", onPageShow);

  return async () => {
    options.target.removeEventListener("pagehide", onPageHide);
    options.target.removeEventListener("pageshow", onPageShow);
    onPageHide();
    await released;
  };
}

function isPersistedRestore(event: Event): boolean {
  return (event as PageTransitionEvent).persisted === true;
}
